import {
  assertGraphQLRequestError,
  consumeMockRestResponse,
} from "@myvp/shared/src/functions/mock"
import { sleep, sleepIfNeeded } from "@myvp/shared/src/functions/sleep"
import { HttpCode } from "@myvp/shared/src/types"
import { isStringEnumValue } from "@myvp/shared/src/functions/type-guards"
import * as service from "src/services/auth.service"
import { routeNames } from "src/router/route-names"
import { mockTimeConstants } from "@myvp/shared/src/constants/mock-time.constants"
import { Role } from "src/gql/graphql"

const virtualTimeAllowed =
  new URLSearchParams(window.location.search)
    .get("time-travel")
    ?.toLowerCase() === "true"

const userStudyRoles = [
  {
    relatedUserRoleId: "7175f4aa-78c9-49b0-90ea-8b48966b486b",
    role: Role.Participant,
    studyDetails: {
      studyId: "default-study",
      studyName: "FEZZIK-07",
    },
    participantDetails: {
      relatedUserId: "current-user-id",
      firstName: "Ian",
      lastName: "Kehl",
      siteVaultParticipantId: "participant-00",
    },
    isDefaultStudy: false,
    toDoCount: 30,
    languageTag: "en-US",
    timeZoneLabel: "(GMT-4:00) Eastern Daylight Time (America/New_York)",
  },
  {
    relatedUserRoleId: "e7c868ad-f1c6-4dfa-972a-39c5b4a1525a",
    role: Role.Proxy,
    studyDetails: {
      studyId: "default-study",
      studyName: "FEZZIK-07",
    },
    participantDetails: {
      relatedUserId: "related-user-id",
      firstName: "Lucy",
      lastName: "Kehl",
      siteVaultParticipantId: "participant-00",
    },
    isDefaultStudy: true,
    toDoCount: 30,
    languageTag: "en-US",
    timeZoneLabel: "(GMT-4:00) Eastern Daylight Time (America/New_York)",
  },
  {
    relatedUserRoleId: "8dd7ecec-7195-4d1e-a3f0-48205177e354",
    role: Role.LarGuardian,
    studyDetails: {
      studyId: "choice-question-study",
      studyName: "Choice Question Study",
    },
    participantDetails: {
      relatedUserId: "related-user-id",
      firstName: "Bruce",
      lastName: "Kehl",
      siteVaultParticipantId: "participant-02",
    },
    isDefaultStudy: false,
    toDoCount: 30,
    languageTag: "en-US",
    timeZoneLabel: "(GMT-4:00) Eastern Daylight Time (America/New_York)",
  },
]

const loginMockData = {
  notEnoughInfo: {
    status: HttpCode.PreconditionFailed,
  },
  "email@email.com": {
    status: 200,
    email: "email@email.com",
    isUserPresent: true,
    phoneNumber: "+11112223344",
    formattedPhoneNumber: "+1 (111) 222-3344",
    id: "id-1",
    firstName: "User",
    lastName: "Name",
    language: "",
    locale: "",
    acctVerified: true,
  },
  "success@veeva.com": {
    status: 200,
    email: "success@veeva.com",
    isUserPresent: true,
    phoneNumber: "+11112223344",
    formattedPhoneNumber: "+1 111 222-3344",
    id: "2e6c1599-0335-4977-83ab-b7e744656edf",
    firstName: "User",
    lastName: "Name",
    acctVerified: true,
  },
  "success-no-email": {
    status: 200,
    isUserPresent: true,
    phoneNumber: "+11112223344",
    formattedPhoneNumber: "+1 111 222-3344",
    id: "2e6c1599-0335-4977-83ab-b7e744656edf",
    firstName: "User",
    lastName: "Name",
    acctVerified: true,
  },
  "success-no-phone": {
    status: 200,
    email: "success-no-phone@veeva.com",
    isUserPresent: true,
    id: "2e6c1599-0335-4977-83ab-b7e744656edf",
    firstName: "User",
    lastName: "Name",
    acctVerified: true,
  },
  "failure@veeva.com": {
    status: 401,
  },
  "matt@james.com": {
    status: HttpCode.Ok,
    email: "matt@james.com",
    isUserPresent: true,
    phoneNumber: "+13424441288",
    id: "2e6c1599-0335-4977-83ab-b7e744656edf",
    firstName: "Lebron",
    lastName: "James",
    language: "",
    locale: "",
    acctVerified: true,
  },
  "jane.doe@email.com": {
    status: 200,
    email: "jane.doe@email.com",
    isUserPresent: true,
    phoneNumber: "+12345678900",
    formattedPhoneNumber: "+1 (234) 567-8900",
    id: "2e6c1599-0335-4977-83ab-b7e744656edf",
    firstName: "Jane",
    lastName: "Doe",
    acctVerified: true,
  },
}

const inPersonLoginMockData = {
  default: {
    status: HttpCode.Ok,
    acctVerified: true,
    language: "English",
    mfaSent: false,
  },
  unauthorized: {
    status: HttpCode.Unauthorized,
    acctVerified: true,
    language: "English",
    mfaSent: false,
  },
}

const getUserInfoMockData = {
  userInfo: {
    email: "joe.shmoe@merck.com",
    formattedPhoneNumber: "1 342-444-1288",
    phoneNumber: "+13424441288",
    timeZoneId: "America/New_York",
    username: "iLoveVern",
    newTermsVersion: null,
    authId: "auth-id",
  },
  userStudies: {
    userStudyRoles,
  },
  getDoesAccountUseVirtualTime: { virtualTimeAllowed },
}
const validateEmailCodeMockData = {
  expired: {
    status: HttpCode.BadRequest,
  },
  "123": {
    status: HttpCode.Ok,
  },
}

export const resendMfaCode: typeof service.resendMfaCode = async () => {
  let start = Date.now()
  await sleepIfNeeded(mockTimeConstants.smallWaitTime, start)
}

export const login: typeof service.login = async (input) => {
  const result = loginMockData[input.username as keyof typeof loginMockData]
  return consumeMockRestResponse(result)
}

export const inPersonLogin: typeof service.inPersonLogin = async () => {
  const searchParams = new URLSearchParams(window.location.search)
  const notificationId = searchParams.get("notificationId")
  const result =
    inPersonLoginMockData[
      notificationId as keyof typeof inPersonLoginMockData
    ] ?? inPersonLoginMockData.default
  consumeMockRestResponse(result)
  return result
}

export const reauth: typeof service.reauth = async (input) => {
  if (input.password === "wrong") {
    throw { status: HttpCode.Unauthorized, acctVerified: false }
  } else if (input.password === "error") {
    throw { status: HttpCode.ServerError, acctVerified: false }
  }
  return { status: HttpCode.Ok, acctVerified: true }
}

export const requestForgotPasswordCode: typeof service.requestForgotPasswordCode =
  async () => {
    return { status: HttpCode.Ok }
  }

export const enterMfaCode: typeof service.enterMfaCode = async () => {
  await sleep(mockTimeConstants.smallWaitTime)
  return { status: HttpCode.Ok }
}

export const verifyMfaCode: typeof service.verifyMfaCode = async () => {
  return { status: HttpCode.Ok }
}

export const logout: typeof service.logout = async () => {
  return {}
}

export const getUserInfo: typeof service.getUserInfo = async () => {
  let result = structuredClone(getUserInfoMockData)
  const searchParams = new URLSearchParams(window.location.search)
  const locale = searchParams.get("locale")
  if (locale) {
    result = {
      ...result,
      userStudies: {
        ...result.userStudies,
        userStudyRoles: result.userStudies.userStudyRoles.map((role: any) => ({
          ...role,
          languageTag: locale,
        })),
      },
    }
  }
  if (
    // so that we only get the modal on the to do page
    // (easier for app screenshot generation)
    result.userInfo.newTermsVersion &&
    window.location.pathname !== routeNames.todo()
  ) {
    result.userInfo.newTermsVersion = null
  }
  return result
}

export const getUserMetadata: typeof service.getUserMetadata = async () => {
  return undefined
}

export const validateEmailCode: typeof service.validateEmailCode = async ({
  code,
}) => {
  const result = validateEmailCodeMockData[
    code as keyof typeof validateEmailCodeMockData
  ] ?? {
    status: HttpCode.Ok,
  }
  return consumeMockRestResponse(result)
}

export const validateNewEmailCode: typeof service.validateNewEmailCode =
  async ({ code }) => {
    if (code === "500") {
      throw { status: HttpCode.ServerError }
    }
    return { status: HttpCode.Ok }
  }

export const userDiscovery: typeof service.userDiscovery = async () => {
  return { status: HttpCode.Ok }
}

export const updatePhoneNumber: typeof service.updatePhoneNumber = async (
  args
) => {
  await sleep(mockTimeConstants.smallWaitTime)
  switch (args.updatedPhoneNumber) {
    case "+14009999999":
      throw { status: HttpCode.BadRequest }
    case "+14019999999":
      throw { status: HttpCode.Unauthorized }
    case "+14129999999":
      throw { status: HttpCode.PreconditionFailed }
    case "+14299999999":
      throw { status: HttpCode.TooManyRequests }
    case "+15009999999":
      throw { status: HttpCode.ServerError }
    default:
      return { status: HttpCode.Ok }
  }
}

export const verifyUpdatedPhoneNumber: typeof service.verifyUpdatedPhoneNumber =
  async (args) => {
    await sleep(mockTimeConstants.smallWaitTime)
    const inputCode = args.code.slice(0, 3)
    if (
      inputCode === args.code.slice(3) &&
      isStringEnumValue(HttpCode)(inputCode)
    ) {
      return consumeMockRestResponse({ status: inputCode })
    } else {
      return { status: HttpCode.Ok }
    }
  }

export const verifyEmailMfa: typeof service.verifyEmailMfa = async () => {
  const mockHeaders = new Headers()
  mockHeaders.set(
    "x-atkn",
    // jwt with ver: "true"
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ2ZXIiOnRydWV9.AT1s6uJFE3X9P7Bq88ExsAQfPnwLkPg1Z7lihjHx0cc"
  )
  return { status: HttpCode.Ok, _httpHeaders: mockHeaders }
}

export const verifyEmail: typeof service.verifyEmail = async () => {
  return { status: HttpCode.Ok }
}

export const updateEmail: typeof service.updateEmail = async ({ newEmail }) => {
  if (newEmail === "500@500.com") {
    throw { status: HttpCode.ServerError }
  }
  return { status: HttpCode.Ok }
}

export const isValidVaultSession: typeof service.isValidVaultSession =
  async () => {
    return { status: HttpCode.Ok }
  }

export const setUsername: typeof service.setUsername = async (username) => {
  switch (username) {
    case "unauthorized":
      throw { status: HttpCode.Unauthorized }
    case "conflict":
      throw { status: HttpCode.Conflict }
    case "invalid":
      throw { status: HttpCode.BadRequest }
    case "error":
      throw { status: HttpCode.ServerError }
    default:
      return { status: HttpCode.Ok }
  }
}

export const setPassword: typeof service.setPassword = async (password) => {
  if (password === "Test400!") {
    throw { status: HttpCode.BadRequest }
  } else if (password === "Test401!") {
    throw { status: HttpCode.Unauthorized }
  } else if (password === "Test500!") {
    throw { status: HttpCode.ServerError }
  }
  return {
    status: HttpCode.Ok,
    phoneCountryCode: "+1",
    validatedPhoneNum: password === "noUserInfo1!" ? "" : "1234567890",
    email: password === "noUserInfo1!" ? "" : "testuser@veeva.com",
  }
}

export const setEmail: typeof service.setEmail = async (email) => {
  if (email === "error400@veeva.com") {
    throw { status: HttpCode.BadRequest }
  } else if (email === "error401@veeva.com") {
    throw { status: HttpCode.Unauthorized }
  } else if (email === "error500@veeva.com") {
    throw { status: HttpCode.ServerError }
  }
  return { status: HttpCode.Ok }
}

export const validateYob: typeof service.validateYob = async (input) => {
  await sleep(mockTimeConstants.normalWaitTime)
  switch (input.yearOfBirth) {
    case "1965":
      throw {
        response: {
          errors: [{ extensions: { errorCode: HttpCode.BadRequest } }],
        },
      }
    case "1966":
      throw {
        response: {
          errors: [{ extensions: { errorCode: HttpCode.Unauthorized } }],
        },
      }
    case "1967":
      throw {
        response: {
          errors: [{ extensions: { errorCode: HttpCode.PreconditionFailed } }],
        },
      }
    case "1968":
      throw {
        response: {
          errors: [{ extensions: { errorCode: HttpCode.Locked } }],
        },
      }
    case "1969":
      throw {
        response: {
          errors: [{ extensions: { errorCode: HttpCode.ServerError } }],
        },
      }
  }
  return { success: true }
}

export const authorizeMfaCodes: typeof service.authorizeMfaCodes = async (
  args
) => {
  const inputCode = args.mfaCode.slice(0, 3)
  if (
    inputCode === args.mfaCode.slice(3) &&
    isStringEnumValue(HttpCode)(inputCode)
  ) {
    return consumeMockRestResponse({ status: inputCode })
  } else {
    return { status: HttpCode.Ok }
  }
}

export const validateNewActivationCode: typeof service.validateNewActivationCode =
  async (activationCode) => {
    const fakeUserResponse = {
      relatedUserId: "fake",
      relatedUserRoleId: "fake",
      dobRequired: false,
      authId: "fake",
    }
    if (activationCode === "ZZZZZZ") {
      assertGraphQLRequestError({
        response: {
          errors: [{ extensions: { errorCode: HttpCode.BadRequest } }],
        },
      })
      return fakeUserResponse
    } else if (activationCode === "XXXXXX") {
      assertGraphQLRequestError({
        response: {
          errors: [{ extensions: { errorCode: HttpCode.ServerError } }],
        },
      })
      return fakeUserResponse
    } else if (activationCode === "YYYYYY") {
      assertGraphQLRequestError({
        response: {
          errors: [{ extensions: { errorCode: HttpCode.PreconditionFailed } }],
        },
      })
      return fakeUserResponse
    } else if (activationCode === "UUUUUU") {
      // Can only happen in activate new study
      assertGraphQLRequestError({
        response: {
          errors: [{ extensions: { errorCode: HttpCode.Unauthorized } }],
        },
      })
      return fakeUserResponse
    } else if (activationCode === "DOBDOB") {
      return {
        relatedUserId: "578daf5a-0ea3-484e-88fa-f53db1ef2206",
        relatedUserRoleId: "27431890-7c05-4024-9ea8-ea1c6b4d7ad7",
        dobRequired: true,
        authId: "a240cc48-2da5-4e2a-b953-fc8e46ac3771",
      }
    } else if (activationCode === "PIIPII") {
      // Can only happen in activate new study
      assertGraphQLRequestError({
        response: {
          errors: [{ extensions: { errorCode: HttpCode.Forbidden } }],
        },
      })
      return fakeUserResponse
    } else {
      return {
        relatedUserId: "578daf5a-0ea3-484e-88fa-f53db1ef2206",
        relatedUserRoleId: "27431890-7c05-4024-9ea8-ea1c6b4d7ad7",
        dobRequired: false,
        authId: "a240cc48-2da5-4e2a-b953-fc8e46ac3771",
      }
    }
  }

export const confirmTerms: typeof service.confirmTerms = async (
  versionNumber
) => {
  switch (versionNumber) {
    case "":
      throw { status: HttpCode.Unauthorized }
    case "invalid":
      throw { status: HttpCode.BadRequest }
    default:
      return { status: HttpCode.Ok }
  }
}

export const authRequest: typeof service.authRequest = async () => {}
export const persistUserMetadata: typeof service.persistUserMetadata = () => {}
export const removeUserMetadata: typeof service.removeUserMetadata = () => {}

export const authMock = {
  userStudyRoles,
}
